import { useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname } from 'src/routes/hooks';

import { useTranslate } from 'src/locales';

import { toast } from 'src/components/snackbar';

import type { LoginError } from '../type';

// Error message mapping for better maintainability
const ERROR_MESSAGE_MAP = {
  'Tenant is not ACTIVE': 'TEANT_USER_NOT_ACTIVE',
  'User is not active': 'TEANT_USER_NOT_ACTIVE',
  'Email Not Verified': 'EMAIL_NOT_VERIFIED',
  'Invalid credentials': 'INVALID_CREDS',
  'Tenant Access denied': 'Tenant Access denied', // Not translated
  'User not found': 'PLS_REGISTER_FIRST',
  'Invalid or expired token': 'INVALID_OR_EXPIRED_TOKEN', 
} as const;

/**
 * Custom hook to handle login errors with centralized error mapping
 * @returns Memoized error handler function
 */
export function useLoginErrorHandler() {
  const router = useRouter();
  const { t } = useTranslate('translation');

  const pathname = usePathname();

  const handleLoginError = useCallback(
    (error: LoginError, options?: { shouldRedirect?: boolean }) => {
      const { shouldRedirect = true } = options || {};

      if (error?.data) {
        const errorData = error.data;
        const message = typeof errorData === 'object' ? errorData.message : undefined;
        const success = typeof errorData === 'object' ? errorData.success : undefined;

        const EMAIL_NOT_VERIFIED_MESSAGES = [
          'Email Not Verified',
          'Email Not Verified, Please Verify OTP',
        ];

        // 1. Check for specific messages that should override generic status codes
        if (message) {
          // Check for Email Verification specifically
          if (EMAIL_NOT_VERIFIED_MESSAGES.includes(message) || message.includes('Email Not Verified')) {
            toast.error(t('EMAIL_NOT_VERIFIED'));
            if (shouldRedirect) {
              const queryString = new URLSearchParams({ returnTo: pathname }).toString();
              const redirectPath = `${paths.auth.verify}?${queryString}`;
              router.replace(redirectPath);
            }
            return;
          }

          // Check if message exists in our error map
          const translationKey = ERROR_MESSAGE_MAP[message as keyof typeof ERROR_MESSAGE_MAP];
          if (translationKey) {
            toast.error(t(translationKey));
            if (shouldRedirect) router.replace(paths.auth.signIn);
            return;
          }
        }

        // 2. Handle 401 unauthorized errors if no specific message matched
        if (error.originalStatus === 401 || (error as any).status === 401) {
          toast.error(t('TEANT_USER_NOT_ACTIVE'));
          if (shouldRedirect) router.replace(paths.auth.signIn);
          return;
        }

        // 3. Handle string error data
        if (typeof errorData === 'string') {
          if (errorData === 'Tenant Access denied') {
            toast.error('Tenant Access denied');
          } else {
            toast.error(t('SOMETHING_WENT_WRONG'));
          }
          if (shouldRedirect) router.replace(paths.auth.signIn);
          return;
        }

        // 4. Fallback for other errors with message
        if (message) {
          if (!success && message === 'Invalid credentials') {
            toast.error(t('INVALID_CREDS'));
          } else {
            toast.error(message || t('SOMETHING_WENT_WRONG'));
          }
        } else {
          toast.error(t('SOMETHING_WENT_WRONG'));
        }
      } else {
        toast.error(t('PLS_TRY_LATER'));
      }

      if (shouldRedirect) router.replace(paths.auth.signIn);
    },
    [router, t, pathname]
  );

  return { handleLoginError };
}
